import React from 'react';
import { Link } from 'react-router-dom';
import styles from './ShippingPolicyPage.module.css';

const ShippingPolicyPage: React.FC = () => {
  return (
    <div className={styles.shippingPolicyPage}>
      <div className={styles.shippingPolicyContainer}>
        {/* Header */}
        <div className={styles.shippingPolicyHeader}>
          <div className={styles.shippingPolicyIcon}>
            <svg
              className="w-8 h-8 text-white flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ maxWidth: '32px', maxHeight: '32px' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h1 className={styles.shippingPolicyTitle}>Shipping Policy</h1>
          <p className={styles.shippingPolicySubtitle}>
            Everything you need to know about our shipping and production
            process.
          </p>
        </div>

        {/* Policy Content */}
        <div className={styles.shippingPolicyContent}>
          <div className={styles.shippingPolicySection}>
            <h2 className={styles.shippingPolicySectionTitle}>
              Shipping Policy
            </h2>

            <div className={`${styles.shippingPolicyInfoBox} ${styles.blue}`}>
              <h3 className={styles.shippingPolicySectionTitle}>
                Thank You for Your Support!
              </h3>
              <p className={styles.shippingPolicyInfoText}>
                Thank you so much for your interest in our products and for
                supporting our brand! We're truly grateful for each and every
                order and want to ensure you're fully informed about our
                shipping and production process before placing your order.
              </p>
              <p className={styles.shippingPolicyInfoText}>
                Please note that all of our products are made to order. This
                means that we begin production only after the website has
                officially closed for orders. Unlike ready-to-ship items,
                made-to-order products require time to ensure the highest
                quality, accuracy, and craftsmanship.
              </p>
            </div>

            <h3 className={styles.shippingPolicySectionTitle}>
              Production Timeline
            </h3>
            <p className={styles.shippingPolicyText}>
              Once the website closes and all orders are finalized, production
              will begin. From that point, please allow up to 2 to 3 weeks for
              your order to be produced. This timeframe includes material
              sourcing (if needed), printing or manufacturing, quality checks,
              and packaging preparation. We are committed to ensuring that each
              product meets our standards before it leaves our hands.
            </p>
            <p className={styles.shippingPolicyText}>
              Please keep in mind that this 2–3 week window refers to the
              production phase only, not the total time until delivery.
              Production time may vary slightly based on order volume, holidays,
              supplier delays, or unexpected disruptions, but we will do our
              best to keep you updated in the event of any significant changes.
            </p>

            <h3 className={styles.shippingPolicySectionTitle}>
              Shipping Timeline
            </h3>
            <p className={styles.shippingPolicyText}>
              After your order has been produced and carefully packed, it will
              be shipped via the shipping method you selected at checkout. At
              that time, you will receive a confirmation email with tracking
              information so you can monitor your order's journey to you.
            </p>
            <p className={styles.shippingPolicyText}>
              Depending on your location and the carrier used, shipping can take
              an additional 3–7 business days (or longer for international
              orders). Please note that once a package is with the shipping
              carrier, any delays due to weather, customs, or postal service
              issues are unfortunately out of our control, though we are always
              happy to assist in tracking down updates if needed.
            </p>

            <div className={`${styles.shippingPolicyInfoBox} ${styles.green}`}>
              <h4 className={styles.shippingPolicySectionTitle}>
                Combined Production + Shipping Timeline
              </h4>
              <p className={styles.shippingPolicyInfoText}>
                From the time the website closes, you can generally expect to
                receive your order in approximately 3 to 4 weeks, though we
                always aim to get your items to you as quickly as possible
                without compromising quality.
              </p>
            </div>

            <h3 className={styles.shippingPolicySectionTitle}>
              Important Reminders
            </h3>
            <div className={`${styles.shippingPolicyInfoBox} ${styles.yellow}`}>
              <ul className={styles.shippingPolicyList}>
                <li>
                  <strong>Address Verification:</strong> Please double-check
                  your shipping address before completing your order. We are not
                  responsible for orders shipped to incorrect or incomplete
                  addresses.
                </li>
                <li>
                  <strong>Gift Orders & Deadlines:</strong> If you are ordering
                  a gift or need your item by a specific date, we recommend
                  reaching out to us in advance so we can give you a realistic
                  estimate.
                </li>
                <li>
                  <strong>Final Sales:</strong> All sales are final once the
                  website closes, as production begins based on the total number
                  of orders received.
                </li>
              </ul>
            </div>

            <div className={`${styles.shippingPolicyInfoBox} ${styles.purple}`}>
              <h4 className={styles.shippingPolicySectionTitle}>
                Our Commitment to You
              </h4>
              <p className={styles.shippingPolicyInfoText}>
                We appreciate your patience and understanding as we work hard to
                create something special just for you. By choosing
                made-to-order, you're supporting a more sustainable and
                intentional way of doing business—and we are so grateful to have
                you as part of that mission.
              </p>
              <p className={styles.shippingPolicyInfoText}>
                If you have any questions, concerns, or special requests
                regarding your order, don't hesitate to{' '}
                <Link to="/contact" className={styles.shippingPolicyLink}>
                  contact us
                </Link>
                . We're always here to help.
              </p>
            </div>

            <div className={styles.shippingPolicyListBox}>
              <h4 className={styles.shippingPolicySectionTitle}>
                Key Points Summary
              </h4>
              <ul className={styles.shippingPolicyList}>
                <li>All products are made to order after the website closes</li>
                <li>Production takes 2-3 weeks</li>
                <li>
                  Shipping takes 3-7 business days (longer for international)
                </li>
                <li>Total timeline: approximately 3-4 weeks</li>
                <li>Made-to-order supports sustainable business practices</li>
                <li>We provide tracking information once shipped</li>
                <li>Contact us for any questions or special requests</li>
              </ul>
            </div>

            <div className={styles.shippingPolicyThankYou}>
              <h4 className={styles.shippingPolicyThankYouTitle}>
                Thank You Again for Your Support!
              </h4>
              <p className={styles.shippingPolicyThankYouText}>
                We're grateful to have you as part of our community and look
                forward to creating something special just for you.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Shopping */}
        <div className={styles.shippingPolicyBackButton}>
          <Link to="/" className={styles.backToShoppingButton}>
            <svg
              className={styles.backToShoppingIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span>Back to Shopping</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicyPage;
